import { useEffect, useId, useRef, useState } from 'react';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';
import { TEXTOS_PIEZAS, type IdiomaPieza } from './textosPiezas';
import './piezas.css';

/**
 * RV Selector (componentes.md, «RV Selector rápido» y «RV Selector lista»): el valor de un campo con muchas opciones
 * (negocio, zona horaria, idioma, país, moneda). Píldora de 58 con la etiqueta pequeña y el valor; con 2 a 4 opciones,
 * bandeja rápida; con más, lista blanca con buscador (más de 8) y la marca de la elegida. Esc o tocar fuera la cierran.
 * `idioma` (G928, G996) pone en inglés «Search» y «No results for “…”.».
 */
export interface OpcionSelector {
    valor: string;
    texto: string;
    icono?: NombreIcono;
    /** Una línea gris debajo (también entra en la búsqueda). */
    detalle?: string;
}

interface Props {
    etiqueta: string;
    valor: string;
    opciones: readonly OpcionSelector[];
    onChange: (valor: string) => void;
    /** `rapido` (2–4 opciones) o `lista`; por defecto, según cuántas haya. */
    modo?: 'rapido' | 'lista';
    /** Buscador en la lista; por defecto, con más de 8 opciones. */
    buscar?: boolean;
    bloqueado?: boolean;
    idioma?: IdiomaPieza;
}

export function Selector({ etiqueta, valor, opciones, onChange, modo, buscar, bloqueado = false, idioma = 'es' }: Props) {
    const t = TEXTOS_PIEZAS[idioma];
    const [abierto, setAbierto] = useState(false);
    const [q, setQ] = useState('');
    const caja = useRef<HTMLDivElement>(null);
    const id = useId();
    const actual = opciones.find((o) => o.valor === valor) ?? opciones[0];
    const rapido = modo === undefined ? opciones.length <= 4 : modo === 'rapido';
    const conBuscador = !rapido && (buscar ?? opciones.length > 8);
    const consulta = q.trim().toLocaleLowerCase(idioma);
    const visibles = !conBuscador || consulta === '' ? opciones : opciones.filter((o) => `${o.texto} ${o.detalle ?? ''}`.toLocaleLowerCase(idioma).includes(consulta));

    useEffect(() => {
        if (!abierto) {
            return;
        }
        const fuera = (e: MouseEvent) => {
            if (caja.current !== null && !caja.current.contains(e.target as Node)) {
                setAbierto(false);
            }
        };
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setAbierto(false);
            }
        };
        document.addEventListener('mousedown', fuera);
        document.addEventListener('keydown', tecla);
        return () => {
            document.removeEventListener('mousedown', fuera);
            document.removeEventListener('keydown', tecla);
        };
    }, [abierto]);

    const elegir = (o: OpcionSelector) => {
        setAbierto(false);
        setQ('');
        if (o.valor !== valor) {
            onChange(o.valor);
        }
    };

    return (
        <div className="rv-selector" ref={caja}>
            <button
                type="button"
                className={`rv-selector__boton${abierto ? ' rv-selector__boton--abierto' : ''}`}
                disabled={bloqueado}
                aria-haspopup="listbox"
                aria-expanded={abierto}
                aria-controls={abierto ? id : undefined}
                onClick={() => { setAbierto((a) => !a); }}
            >
                {actual?.icono !== undefined && <Icono nombre={actual.icono} />}
                <span className="rv-selector__valor">
                    <small>{etiqueta}</small>
                    <span>{actual?.texto ?? ''}</span>
                </span>
                {!bloqueado && <Icono nombre="chevron-down" />}
            </button>
            {abierto && (
                <div className={`rv-selector__panel${rapido ? ' rv-selector__panel--rapido' : ''}`}>
                    {conBuscador && (
                        <label className="rv-selector__buscar">
                            <Icono nombre="search" />
                            <input type="search" value={q} placeholder={t.buscar} aria-label={t.buscar} onChange={(e) => { setQ(e.target.value); }} />
                        </label>
                    )}
                    <div id={id} role="listbox" aria-label={etiqueta} className="rv-selector__lista">
                        {visibles.map((o) => (
                            <button key={o.valor} type="button" role="option" aria-selected={o.valor === actual?.valor} className="rv-selector__opcion" onClick={() => { elegir(o); }}>
                                {o.icono !== undefined && <Icono nombre={o.icono} />}
                                <span className="rv-selector__texto">
                                    <span>{o.texto}</span>
                                    {o.detalle !== undefined && o.detalle !== '' && <small>{o.detalle}</small>}
                                </span>
                                {!rapido && o.valor === actual?.valor && <Icono nombre="check" />}
                            </button>
                        ))}
                        {visibles.length === 0 && <p className="rv-selector__vacio">{t.sinResultados(q.trim())}</p>}
                    </div>
                </div>
            )}
        </div>
    );
}
